import BaseSection from '@/sections/base'
import gsap from '@/core/gsap'
import { prefersReducedMotion } from '@/core/utils/a11y'
import { dispatch } from '@/core/utils/event'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

const selectors = {
  overlay: '[data-intro-overlay]',
  cell: '[data-intro-cell]'
}

// Signals page-hero to fade its newsletter in as the overlay lifts
const REVEAL_EVENT = 'intro.reveal'

// Signals deferred UI (e.g. the cookie banner) that the overlay is fully gone
const COMPLETE_EVENT = 'intro.complete'

// Fallback dismissal so the fixed overlay can never trap the page
const SAFETY_MS = 6000

export default class IntroOverlaySection extends BaseSection {
  static TYPE = 'intro-overlay'

  overlay: HTMLElement | undefined
  cells: HTMLElement[]
  tl: gsap.core.Timeline | null
  safetyTimeout: number

  constructor(container: HTMLElement) {
    super(container)

    this.overlay = this.qs(selectors.overlay)
    this.cells = this.qsa(selectors.cell)
    this.tl = null
    this.safetyTimeout = 0

    if (!this.overlay || this.cells.length === 0) return

    // The Taxi page-view wrapper gets a `transform` during transitions, which
    // disables `backdrop-filter` on descendants. Reparent to <body> (alongside
    // the cart/modal glass surfaces) so the blur actually renders.
    document.body.appendChild(this.overlay)

    this.play()
  }

  play() {
    this.safetyTimeout = window.setTimeout(() => this.dismiss(), SAFETY_MS)

    const base = this.letterOpacity()

    if (prefersReducedMotion()) {
      gsap.set(this.cells, { opacity: base })
      this.tl = gsap
        .timeline({ delay: 0.4 })
        .to(this.overlay!, {
          opacity: 0,
          duration: 0.4,
          onStart: () => dispatch(REVEAL_EVENT),
          onComplete: () => this.hide()
        })
      return
    }

    gsap.set(this.cells, { opacity: 0 })

    // Wavy diagonal flow: top-left → bottom-right, with an undulating (non-straight) wavefront
    const revealDelays = this.diagonalDelays(1.0)
    const shimmerDelays = this.diagonalDelays(1.4)

    this.tl = gsap.timeline()

    // 1 — Reveal: the pattern fades in along the diagonal (no movement)
    this.tl.to(this.cells, {
      opacity: base,
      duration: 0.5,
      ease: 'power1.out',
      stagger: (i: number) => revealDelays[i]
    })

    // 2 — "Thinking" shimmer: a bright band flows fluidly from corner to corner.
    // Each cell pulses up then back (repeat+yoyo); the per-cell stagger delay drives the travel.
    this.tl.to(this.cells, {
      opacity: 1,
      duration: 0.45,
      ease: 'sine.inOut',
      repeat: 1,
      yoyo: true,
      stagger: (i: number) => shimmerDelays[i]
    }, '>-0.3')

    // 3 — Lift away to reveal the page
    this.tl.to(this.overlay!, {
      opacity: 0,
      duration: 0.6,
      ease: 'power2.inOut',
      onStart: () => dispatch(REVEAL_EVENT),
      onComplete: () => this.hide()
    }, '>-0.1')
  }

  /**
   * Per-cell delays that make an animation flow from the top-left corner to the
   * bottom-right corner. The wavefront undulates (a sine offset per row) so the
   * band travels on a curved, fluid diagonal rather than a straight line.
   *
   * @param spread - total time (s) between the first and last cell
   */
  diagonalDelays(spread: number): number[] {
    const points = this.cells.map(cell => ({
      x: cell.offsetLeft + cell.offsetWidth / 2,
      y: cell.offsetTop + cell.offsetHeight / 2
    }))

    const xs = points.map(p => p.x)
    const ys = points.map(p => p.y)
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)
    const rangeX = Math.max(...xs) - minX || 1
    const rangeY = Math.max(...ys) - minY || 1

    const WAVE_AMPLITUDE = 0.12
    const WAVE_FREQUENCY = 1.6

    return points.map(p => {
      const nx = (p.x - minX) / rangeX
      const ny = (p.y - minY) / rangeY
      const diagonal = (nx + ny) / 2
      const undulation = Math.sin(ny * Math.PI * WAVE_FREQUENCY) * WAVE_AMPLITUDE

      return Math.max(0, diagonal + undulation) * spread
    })
  }

  letterOpacity(): number {
    const value = getComputedStyle(this.overlay!).getPropertyValue('--intro-letter-opacity')
    const parsed = parseFloat(value)

    return Number.isFinite(parsed) ? parsed : 0.45
  }

  dismiss() {
    this.tl?.kill()
    this.hide()
  }

  hide() {
    window.clearTimeout(this.safetyTimeout)
    // Reparented to <body>, so remove it explicitly rather than hiding in place
    this.overlay?.remove()
    dispatch(COMPLETE_EVENT)
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    window.clearTimeout(this.safetyTimeout)
    this.tl?.kill()
    this.overlay?.remove()

    super.onUnload(e)
  }
}
